import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';


const SelectArrivalAirportPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState('');
  const [airports, setAirports] = useState([]);
  const [filteredAirports, setFilteredAirports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAirport, setSelectedAirport] = useState('');

  // Get URL parameters
  const depid = searchParams.get('depid');
  const rType = searchParams.get('Rtype');
  const passengers = searchParams.get('pess');
  const departureDate = searchParams.get('d_date');
  const nearbyAirport = searchParams.get('nearby_airport');
  const returnDate = searchParams.get('r_date');

  useEffect(() => {
    // Simulate API call to fetch airports
    setTimeout(() => {
      const mockAirports = [
        { id: 1, code: 'JFK', name: 'John F. Kennedy International Airport', city: 'New York', country: 'United States', type: 'International' },
        { id: 2, code: 'LAX', name: 'Los Angeles International Airport', city: 'Los Angeles', country: 'United States', type: 'International' },
        { id: 3, code: 'LHR', name: 'London Heathrow Airport', city: 'London', country: 'United Kingdom', type: 'International' },
        { id: 4, code: 'CDG', name: 'Charles de Gaulle Airport', city: 'Paris', country: 'France', type: 'International' },
        { id: 5, code: 'NRT', name: 'Narita International Airport', city: 'Tokyo', country: 'Japan', type: 'International' },
        { id: 6, code: 'SIN', name: 'Singapore Changi Airport', city: 'Singapore', country: 'Singapore', type: 'International' },
        { id: 7, code: 'DXB', name: 'Dubai International Airport', city: 'Dubai', country: 'United Arab Emirates', type: 'International' },
        { id: 8, code: 'SYD', name: 'Sydney Kingsford Smith Airport', city: 'Sydney', country: 'Australia', type: 'International' },
        { id: 9, code: 'FRA', name: 'Frankfurt Airport', city: 'Frankfurt', country: 'Germany', type: 'International' },
        { id: 10, code: 'AMS', name: 'Amsterdam Airport Schiphol', city: 'Amsterdam', country: 'Netherlands', type: 'International' },
        { id: 11, code: 'ORD', name: 'Chicago O\'Hare International Airport', city: 'Chicago', country: 'United States', type: 'International' },
        { id: 12, code: 'ATL', name: 'Hartsfield-Jackson Atlanta International Airport', city: 'Atlanta', country: 'United States', type: 'International' },
        { id: 13, code: 'DEN', name: 'Denver International Airport', city: 'Denver', country: 'United States', type: 'International' },
        { id: 14, code: 'MIA', name: 'Miami International Airport', city: 'Miami', country: 'United States', type: 'International' },
        { id: 15, code: 'SEA', name: 'Seattle-Tacoma International Airport', city: 'Seattle', country: 'United States', type: 'International' },
        { id: 16, code: 'BOS', name: 'Logan International Airport', city: 'Boston', country: 'United States', type: 'International' },
        { id: 17, code: 'SFO', name: 'San Francisco International Airport', city: 'San Francisco', country: 'United States', type: 'International' },
        { id: 18, code: 'LAS', name: 'McCarran International Airport', city: 'Las Vegas', country: 'United States', type: 'International' },
        { id: 19, code: 'PHX', name: 'Phoenix Sky Harbor International Airport', city: 'Phoenix', country: 'United States', type: 'International' },
        { id: 20, code: 'IAH', name: 'George Bush Intercontinental Airport', city: 'Houston', country: 'United States', type: 'International' }
      ];

      setAirports(mockAirports);
      setFilteredAirports(mockAirports);
      setLoading(false);
    }, 1000);
  }, []);

  useEffect(() => {
    if (searchTerm) {
      const filtered = airports.filter(airport =>
        airport.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        airport.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
        airport.city.toLowerCase().includes(searchTerm.toLowerCase()) ||
        airport.country.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredAirports(filtered);
    } else {
      setFilteredAirports(airports);
    }
  }, [searchTerm, airports]);

  const handleAirportSelect = (airport) => {
    setSelectedAirport(airport);

    // Navigate to flight search with selected airport
    const params = new URLSearchParams({
      departure: depid || '',
      arrival: airport.id,
      type: rType || 'oneway',
      passengers: passengers || '1',
      departureDate: departureDate || '',
      returnDate: returnDate || '',
      nearbyAirport: nearbyAirport || 'false'
    });

    navigate(`/flight-list?${params.toString()}`);
  };

  const handleBack = () => {
    navigate(-1);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-jet-gray-50 pt-20 pb-10">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-jet-yellow mx-auto mb-4"></div>
            <p className="text-jet-gray-600">Loading airports...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-jet-gray-50 pt-20 pb-10">
      <div className="w-[86%] mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-2xl font-bold text-jet-gray-900">Select Arrival Airport</h1>
            <button
              onClick={handleBack}
              className="bg-jet-gray-900 hover:bg-jet-gray-800 text-white px-4 py-2 rounded-md transition-colors duration-200 flex items-center text-sm font-medium"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back
            </button>
          </div>

          <p className="text-jet-gray-600 mb-4">
            Choose your destination airport from the list below
          </p>

          {/* Search Bar */}
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg className="h-5 w-5 text-jet-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <input
              type="text"
              placeholder="Search airports by name, code, city, or country..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-jet-gray-300 rounded-lg focus:outline-none focus:border-jet-yellow focus:ring-1 focus:ring-jet-yellow"
            />
          </div>
        </div>

        {/* Airport List */}
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <div className="p-4 border-b border-jet-gray-200">
            <h2 className="text-lg font-semibold text-jet-gray-900">
              Available Airports ({filteredAirports.length})
            </h2>
          </div>

          {filteredAirports.length === 0 ? (
            <div className="p-8 text-center">
              <div className="text-6xl mb-4">✈️</div>
              <h3 className="text-lg font-medium text-jet-gray-900 mb-2">No airports found</h3>
              <p className="text-jet-gray-600">
                Try adjusting your search terms or browse all available airports.
              </p>
            </div>
          ) : (
            <div className="divide-y divide-jet-gray-200">
              {filteredAirports.map((airport) => (
                <div
                  key={airport.id}
                  onClick={() => handleAirportSelect(airport)}
                  className="p-4 hover:bg-jet-gray-50 cursor-pointer transition-colors duration-200"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3">
                        <div className="bg-jet-yellow text-jet-gray-900 px-3 py-1 rounded-md text-sm font-bold">
                          {airport.code}
                        </div>
                        <div>
                          <h3 className="text-lg font-medium text-jet-gray-900">
                            {airport.name}
                          </h3>
                          <p className="text-sm text-jet-gray-600">
                            {airport.city}, {airport.country}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center space-x-2">
                      <span className="bg-jet-gray-100 text-jet-gray-700 px-2 py-1 rounded-full text-xs">
                        {airport.type}
                      </span>
                      <svg className="w-5 h-5 text-jet-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Popular Destinations */}
        <div className="mt-8 bg-white rounded-lg shadow-sm p-6">
          <h2 className="text-lg font-semibold text-jet-gray-900 mb-4">Popular Destinations</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {airports.slice(0, 8).map((airport) => (
              <button
                key={airport.id}
                onClick={() => handleAirportSelect(airport)}
                className="p-3 border border-jet-gray-200 rounded-lg hover:border-jet-yellow hover:bg-jet-yellow hover:bg-opacity-10 transition-colors duration-200 text-left"
              >
                <div className="font-bold text-jet-gray-900">{airport.code}</div>
                <div className="text-sm text-jet-gray-600">{airport.city}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Flight Parameters Summary */}
        {(depid || passengers || departureDate) && (
          <div className="mt-6 bg-jet-yellow bg-opacity-10 border border-jet-yellow rounded-lg p-4">
            <h3 className="text-sm font-medium text-jet-gray-900 mb-2">Flight Search Parameters:</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              {depid && (
                <div>
                  <span className="text-jet-gray-600">Departure:</span>
                  <div className="font-medium">{depid}</div>
                </div>
              )}
              {passengers && (
                <div>
                  <span className="text-jet-gray-600">Passengers:</span>
                  <div className="font-medium">{passengers}</div>
                </div>
              )}
              {departureDate && (
                <div>
                  <span className="text-jet-gray-600">Departure Date:</span>
                  <div className="font-medium">{departureDate}</div>
                </div>
              )}
              {returnDate && (
                <div>
                  <span className="text-jet-gray-600">Return Date:</span>
                  <div className="font-medium">{returnDate}</div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SelectArrivalAirportPage;